import { useTasks } from './TaskProvider'
import { Link } from 'react-router-dom'
import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import type { RoomUser } from './types'
import type { LiveDocument } from './LiveDocument'

export type Peer = { connectionId: string; user: Pick<RoomUser, 'id' | 'username' | 'displayName'>; active: boolean; path: string; projectId: string | null; field: { key: string; label: string; anchor?: string; head?: string } | null }
export type LiveMessage = { type: string; projectId?: string; update?: string; vector?: string; revision?: number; createdAt?: string; updatedAt?: string; ack?: number; fields?: string[]; actor?: { id: string; name: string }; error?: string }
type Room = {
  user: RoomUser; connected: boolean; peers: Peer[]; document: React.RefObject<LiveDocument | null>
  send: (message: unknown) => boolean
  listen: (handler: (message: LiveMessage) => void) => () => void
  field: (value: Peer['field']) => void
}
const Context = createContext<Room | null>(null)
export const useLiveRoom = () => useContext(Context)!
export function LiveProvider({ user, children }: { user: RoomUser; children: ReactNode }) {
  const location = useLocation()
  const [connected, setConnected] = useState(false), [peers, setPeers] = useState<Peer[]>([])
  const socket = useRef<WebSocket | null>(null), handlers = useRef(new Set<(m: LiveMessage) => void>())
  const document = useRef<LiveDocument | null>(null)
  const path = useRef(location.pathname + location.search), currentField = useRef<Peer['field']>(null)
  const send = (value: unknown) => {
    if (socket.current?.readyState !== WebSocket.OPEN) return false
    socket.current.send(JSON.stringify(value)); return true
  }
  const announce = () => send({ type: 'presence', presence: { active: window.document.visibilityState === 'visible', path: path.current, projectId: new URLSearchParams(path.current.split('?')[1]).get('library') === '0' ? document.current?.projectId : null, field: currentField.current } })
  useEffect(() => {
    path.current = new URLSearchParams(location.search).has('tasks') ? '/?tasks=1' : location.pathname + location.search
    currentField.current = null; announce()
  }, [location.pathname, location.search])
  useEffect(() => {
    let cancelled = false, suspended = false, retry = 0, attempts = 0
    const connect = () => {
      if (cancelled || suspended) return
      const url = new URL('/api/live', window.location.href)
      url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:'
      url.search = new URLSearchParams({ user: user.id, client: '1', media: '1' }).toString()
      const ws = new WebSocket(url); socket.current = ws
      ws.onmessage = event => {
        if (cancelled || suspended || socket.current !== ws) return
        let message
        try { message = JSON.parse(event.data) } catch { return }
        if (message.type === 'connected') { attempts = 0; setConnected(true); announce() }
        if (message.type === 'presence') setPeers(message.peers)
        if (message.type === 'chat-changed') window.dispatchEvent(new Event('bramble:chat-changed'))
        if (message.type === 'room-changed' || message.type === 'changed') window.dispatchEvent(new CustomEvent('bramble:remote-change', { detail: message }))
        for (const listener of handlers.current) listener(message)
      }
      ws.onclose = event => {
        if (cancelled || suspended || socket.current !== ws) return
        setConnected(false); setPeers([])
        if (event.code === 4001) { window.dispatchEvent(new Event('bramble:unauthorized')); return }
        retry = window.setTimeout(connect, Math.min(15_000, 500 * 2 ** attempts++) + Math.random() * 300)
      }
      ws.onerror = () => ws.close()
    }
    connect()
    const hide = () => {
      // An outgoing document must not start refresh requests from a queued
      // socket message. WebKit rejects those requests during navigation.
      suspended = true
      window.clearTimeout(retry)
      socket.current?.close()
      setConnected(false); setPeers([])
    }
    const show = () => { if (suspended && !cancelled) { suspended = false; attempts = 0; connect() } }
    const visible = () => { if (window.document.visibilityState !== 'visible') currentField.current = null; announce() }
    const timer = window.setInterval(announce, 20_000)
    window.addEventListener('pagehide', hide)
    window.addEventListener('pageshow', show)
    window.document.addEventListener('visibilitychange', visible)
    return () => { cancelled = true; window.clearTimeout(retry); window.clearInterval(timer); socket.current?.close(); window.removeEventListener('pagehide', hide); window.removeEventListener('pageshow', show); window.document.removeEventListener('visibilitychange', visible) }
  }, [user.id])
  return <Context.Provider value={{ user, connected, peers, document, send, listen: handler => { handlers.current.add(handler); return () => { handlers.current.delete(handler) } }, field: value => { currentField.current = value; announce() } }}>{children}</Context.Provider>
}

export function LivePeople() {
  const room = useLiveRoom()
  const { members } = useTasks()
  const peers = members.filter(member => member.id !== room.user.id).map(member => {
    const sessions = room.peers.filter(peer => peer.user.id === member.id)
    return sessions.find(peer => peer.active && peer.field) || sessions.find(peer => peer.active) || sessions[0] || { user: member, active: false, path: '/', field: null, offline: true }
  })
  return <div className="live-people" aria-label="Live collaborators">
    <span className="connection-status" title={room.connected ? 'Live connection ready' : 'Reconnecting. Your draft stays in this browser.'}><i data-active={room.connected} />{room.connected ? 'Live' : 'Reconnecting'}</span>
    {peers.map(peer => <Link key={peer.user.id} className="live-person" to={peer.path} title={`${peer.user.displayName} · ${peer.active ? peer.field ? 'Editing ' + peer.field.label : 'Online' : 'offline' in peer ? 'Offline' : 'Away'} · Open their view`}>
      <i data-active={peer.active} /><b className="live-person-name">{peer.user.displayName}</b><span>{peer.active && peer.field ? 'editing' : peer.active ? 'online' : 'offline' in peer ? 'offline' : 'away'}</span>
    </Link>)}
  </div>
}

export function PresenceDot({ userId }: { userId: string }) {
  const room = useLiveRoom()
  const active = room && (userId === room.user.id ? room.connected : room.peers.some(peer => peer.user.id === userId && peer.active))
  return <i className="profile-presence" data-active={Boolean(active)} title={active ? 'Online now' : 'Not active here'} />
}
